import * as dgram from "node:dgram";
import { clampByte, type LanDevice, type LanMessage, type LanStatus, type TimerAdapter } from "./types";
import { FORCE_COLOR_MODE_SETTLE_MS } from "./timing-constants";
import {
  SEGMENT_BRIGHTNESS_BITMASK_BYTES,
  SEGMENT_COLOR_BITMASK_BYTES,
  SEGMENT_COUNT_MAX,
} from "./device-manager/lookups";

const MULTICAST_ADDR = "239.255.255.250";
const SCAN_PORT = 4001;
const LISTEN_PORT = 4002;
const COMMAND_PORT = 4003;
/** Cap on distinct LAN device identities we track/create — bounds a spoofed-discovery flood (SEC-H2). */
const MAX_DISTINCT_LAN_DEVICES = 512;

/** Callback for discovered LAN devices */
export type LanDiscoveryCallback = (device: LanDevice) => void;

/** Callback for status updates (matched by source IP, not device ID) */
export type LanStatusCallback = (sourceIp: string, status: LanStatus) => void;

/**
 * Callback fired for every outgoing UDP datagram. Wired to the DiagnosticsCollector
 * via main.ts so the report carries the verbatim bytes the adapter put on the
 * wire — closes the "did the adapter even send the snapshot?" diag blind spot.
 *
 * Note: ip is the destination, not a device id. main.ts resolves to deviceId.
 */
export type LanSendCallback = (ip: string, cmd: string, payload: unknown, bytes: number, error?: string) => void;

/**
 * Callback fired for every parsed devStatus reply. Wired to the
 * DiagnosticsCollector so a "device ack'd the command but didn't render"
 * report has the actual response payload in the diag JSON.
 */
export type LanStatusRecordCallback = (sourceIp: string, status: LanStatus) => void;

/**
 * Callback fired for every parsed scan reply. Diag-only side-channel that
 * lets the DiagnosticsCollector log the raw LAN discovery handshake — useful
 * for "device shows up in scan but the adapter doesn't add it" reports.
 */
export type LanScanRecordCallback = (lanDevice: LanDevice) => void;

/**
 * Govee LAN UDP client for device discovery and control.
 * Handles multicast discovery on port 4001, listens on 4002, sends commands to 4003.
 */
export class GoveeLanClient {
  private scanSocket: dgram.Socket | null = null;
  private listenSocket: dgram.Socket | null = null;
  /**
   * Persistent send-socket — previously a new dgram socket was created, used to
   * send, and closed per command. On adapter stop mid-send the callback could
   * fire into a half-torn-down adapter.
   */
  private sendSocket: dgram.Socket | null = null;
  private scanTimer: ioBroker.Interval | undefined = undefined;
  /**
   * True after `stop()` was called — bind-callbacks check this flag before
   * starting timers/scans, so a `stop()` during the async listen+scan bind
   * sequence cannot leave a runaway scanTimer behind.
   */
  private stopped = false;
  /**
   * Pending one-shot timeouts created by {@link flashSingleSegment} — kept
   * so {@link stop} can cancel them before the deferred ptReal burst fires
   * into a torn-down LAN client.
   */
  private readonly pendingFlashTimers = new Set<ioBroker.Timeout>();
  private readonly timers: TimerAdapter;
  private readonly log: ioBroker.Logger;
  private onDiscovery: LanDiscoveryCallback | null = null;
  private onStatus: LanStatusCallback | null = null;
  /**
   * Fired when a socket on a PINNED interface errors (e.g. EADDRNOTAVAIL
   * after a DHCP change killed the selected IP) — the adapter surfaces it
   * as an actionable problem; a debug line alone left the LAN channel dead
   * with the ready-hint pointing users at the Govee app instead of the
   * Network Interface setting (M11).
   */
  public onInterfaceError: ((message: string) => void) | null = null;
  /** Fired when the listen socket is up — resolves a prior interface problem. */
  public onListenReady: (() => void) | null = null;
  /** warn-once guard for non-EADDRINUSE socket errors. */
  private socketErrorWarned = false;
  private onSend: LanSendCallback | null = null;
  private onStatusRecord: LanStatusRecordCallback | null = null;
  private onScanRecord: LanScanRecordCallback | null = null;
  private readonly seenDeviceIps = new Set<string>();
  /** Warn-once latch when the LAN discovery cap is hit (SEC-H2). */
  private lanFloodWarned = false;
  /**
   * Per-IP timestamp of the last command we sent (ptReal/setScene/etc).
   * Used to annotate incoming LAN-status responses with the Δt — gives an
   * approximate "did this status follow a command of ours?" signal in the
   * debug log. Proximity, not proof: Govee's UDP protocol doesn't carry
   * command IDs, so a small Δ is *probably* a response but could be
   * unrelated polling.
   */
  private readonly lastCommandSentMs = new Map<string, number>();
  /** Multicast membership address — remembered for dropMembership in stop(). */
  private multicastBind: string | undefined;

  /**
   * @param log ioBroker logger
   * @param timers Timer adapter for setInterval/setTimeout
   */
  constructor(log: ioBroker.Logger, timers: TimerAdapter) {
    this.log = log;
    this.timers = timers;
  }

  /**
   * Register a send hook called for every outgoing UDP datagram. main.ts
   * resolves the destination IP to a deviceId and forwards into the
   * DiagnosticsCollector — closes the v2.9.0 diag blind spot where ptReal
   * sends were only visible in the adapter log, not in per-device diag.
   *
   * @param cb Callback receiving (ip, cmd, payload, bytes, error?)
   */
  setSendHook(cb: LanSendCallback | null): void {
    this.onSend = cb;
  }

  /**
   * Socket-error funnel: with a pinned interface the error is user-fixable
   * (Network Interface setting) → warn-once + onInterfaceError for the
   * actionable-problems registry. Without pinning it stays a warn-once —
   * still visible (a dead socket kills the LAN channel), but not actionable
   * config-wise.
   *
   * @param kind     Which socket errored (send/listen/scan)
   * @param err      The socket error
   * @param bindAddr The pinned interface IP, if any
   */
  private reportSocketError(kind: string, err: Error, bindAddr: string | undefined): void {
    const msg = `LAN ${kind} socket error: ${err.message}`;
    if (this.socketErrorWarned) {
      this.log.debug(msg);
    } else {
      this.socketErrorWarned = true;
      this.log.warn(msg);
    }
    if (bindAddr) {
      this.onInterfaceError?.(
        `LAN ${kind} socket failed on the selected network interface ${bindAddr} (${err.message}) — check the Network Interface setting; the selected IP may no longer exist`,
      );
    }
  }

  /**
   * Register a hook called for every parsed devStatus reply. Used for diag
   * capture — adapter looks up the device by IP and records the payload as
   * a pseudo-endpoint (`lan://devStatus`).
   *
   * @param cb Callback receiving (sourceIp, status)
   */
  setStatusRecordHook(cb: LanStatusRecordCallback | null): void {
    this.onStatusRecord = cb;
  }

  /**
   * Register a hook called for every parsed scan reply. Diag-only.
   *
   * @param cb Callback receiving (lanDevice)
   */
  setScanRecordHook(cb: LanScanRecordCallback | null): void {
    this.onScanRecord = cb;
  }

  /**
   * Start LAN discovery and listening for responses.
   *
   * @param onDiscovery Called when a new device is found
   * @param onStatus Called when a status response arrives
   * @param scanIntervalMs How often to send multicast scan (default 30s)
   * @param networkInterface IP of network interface to bind to (empty = all)
   */
  start(
    onDiscovery: LanDiscoveryCallback,
    onStatus: LanStatusCallback,
    scanIntervalMs = 30_000,
    networkInterface = "",
  ): void {
    this.onDiscovery = onDiscovery;
    this.onStatus = onStatus;

    const bindAddr = networkInterface && networkInterface !== "0.0.0.0" ? networkInterface : undefined;
    if (bindAddr) {
      this.log.info(`LAN binding to network interface ${bindAddr}`);
    }

    this.multicastBind = bindAddr;

    // Persistent send-socket for commands — create once, close in stop().
    this.sendSocket = dgram.createSocket("udp4");
    this.sendSocket.on("error", err => {
      this.reportSocketError("send", err, bindAddr);
    });
    // Pin the command socket's source to the chosen interface so unicast
    // commands/devStatus egress it AND devices reply to bindAddr:4002 (where
    // listenSocket is bound). Without binding, the OS auto-selects the source on
    // a multi-homed host and replies can miss the listen socket. Skipped for the
    // all-interfaces default (bindAddr undefined) — behaviour unchanged there.
    if (bindAddr) {
      this.sendSocket.bind(0, bindAddr);
    }

    // Listen socket for responses (port 4002) — must be ready before first scan
    this.listenSocket = dgram.createSocket({ type: "udp4", reuseAddr: true });
    this.listenSocket.on("message", (msg, rinfo) => {
      this.handleMessage(msg, rinfo.address);
    });
    this.listenSocket.on("error", err => {
      // EADDRINUSE = port 4002 already taken (a second adapter instance?). The
      // user needs to know — otherwise the adapter is half-dead (discovery via
      // scan works, status replies are lost).
      const code = (err as NodeJS.ErrnoException).code;
      if (code === "EADDRINUSE") {
        this.log.warn(`LAN listen port ${LISTEN_PORT} already in use — second instance? Status updates will be lost.`);
      } else {
        this.reportSocketError("listen", err, bindAddr);
      }
    });
    this.listenSocket.bind(LISTEN_PORT, bindAddr, () => {
      // stop() may have been called between listenSocket.bind and this
      // callback firing — bail out so we don't spin up a scan socket and a
      // recurring timer behind the back of a torn-down LAN client.
      if (this.stopped) {
        return;
      }
      this.log.debug(`LAN listening on port ${LISTEN_PORT}`);
      this.onListenReady?.();

      // Scan socket for multicast discovery (port 4001) — started after listen is ready
      this.scanSocket = dgram.createSocket({ type: "udp4", reuseAddr: true });
      this.scanSocket.on("error", err => {
        this.reportSocketError("scan", err, bindAddr);
      });
      // bind(0, bindAddr) — ephemeral port, but bound to the chosen interface.
      // Previously bind() without an address → ANY → asymmetric to the listen
      // socket that uses bindAddr.
      this.scanSocket.bind(0, bindAddr, () => {
        if (this.stopped) {
          return;
        }
        this.scanSocket?.setBroadcast(true);
        try {
          this.scanSocket?.addMembership(MULTICAST_ADDR, bindAddr);
        } catch {
          // A membership fail is typically an OS routing issue (e.g. interface
          // bindAddr not in the multicast routing table). LAN discovery stays
          // partially functional via setBroadcast(true), but that is asymmetric
          // — replies may not come back. Log at info level so the user can find
          // the cause of the "no devices" symptom in the logs.
          this.log.info(
            `LAN: could not join multicast group on ${bindAddr ?? "default interface"} — discovery may be incomplete`,
          );
        }
        // Pin OUTGOING multicast to the chosen interface. Binding the socket to
        // bindAddr only sets the source address — the multicast egress interface
        // is controlled by IP_MULTICAST_IF (setMulticastInterface); without it the
        // OS picks via its default route, so the discovery scan can leave the
        // wrong interface on a multi-homed host despite an explicit selection
        // (Node dgram docs). Skipped for the all-interfaces default.
        if (bindAddr) {
          try {
            this.scanSocket?.setMulticastInterface(bindAddr);
          } catch {
            this.log.info(
              `LAN: could not pin multicast egress to ${bindAddr} — outgoing discovery may use the default interface`,
            );
          }
        }
        this.sendScan();
      });

      // Periodic scan — guard the timer creation in case stop() raced with
      // the bind callback, otherwise we'd leave a setInterval running
      // against a torn-down LAN client.
      if (!this.stopped) {
        this.scanTimer = this.timers.setInterval(() => {
          this.sendScan();
        }, scanIntervalMs);
      }
    });
  }

  /**
   * Snapshot of the discovery cache + last-command-sent timestamps for
   * the runtime-state diag export. Returns plain serialisable shapes so
   * the DiagnosticsCollector can clone-and-cap them safely.
   */
  getDiagSnapshot(): { seenDeviceIps: string[]; lastCommandSentMs: Record<string, number> } {
    return {
      seenDeviceIps: Array.from(this.seenDeviceIps),
      lastCommandSentMs: Object.fromEntries(this.lastCommandSentMs),
    };
  }

  /** Stop all sockets and timers */
  stop(): void {
    this.stopped = true;
    if (this.scanTimer) {
      this.timers.clearInterval(this.scanTimer);
      this.scanTimer = undefined;
    }
    // Cancel any deferred wizard-flash bursts that haven't fired yet.
    for (const handle of this.pendingFlashTimers) {
      this.timers.clearTimeout(handle);
    }
    this.pendingFlashTimers.clear();
    if (this.scanSocket) {
      // dropMembership symmetric to addMembership — on macOS/Windows the
      // multicast filter could otherwise linger on the NIC until process exit.
      try {
        if (this.multicastBind) {
          this.scanSocket.dropMembership(MULTICAST_ADDR, this.multicastBind);
        }
      } catch {
        /* ignore — best-effort */
      }
      try {
        this.scanSocket.close();
      } catch {
        /* ignore */
      }
      this.scanSocket = null;
    }
    if (this.listenSocket) {
      try {
        this.listenSocket.close();
      } catch {
        /* ignore */
      }
      this.listenSocket = null;
    }
    if (this.sendSocket) {
      try {
        this.sendSocket.close();
      } catch {
        /* ignore */
      }
      this.sendSocket = null;
    }
    // L4 — clear seenDeviceIps, otherwise entries survive between start/stop
    // cycles in the same process (correctness unaffected, but the
    // discovery-log info on an IP change would be lost).
    this.seenDeviceIps.clear();
    // Same for the per-IP command timestamps — symmetric teardown so a
    // start/stop cycle doesn't carry stale Δt annotations into the next run.
    this.lastCommandSentMs.clear();
    this.multicastBind = undefined;
  }

  /**
   * Send a control command to a device via LAN.
   *
   * @param ip Device IP address
   * @param cmd Command name (turn, brightness, colorwc, devStatus)
   * @param data Command data
   */
  private sendCommand(ip: string, cmd: string, data: Record<string, unknown>): void {
    if (!this.sendSocket) {
      this.log.debug(`LAN send dropped (socket not ready): ${cmd} → ${ip}`);
      this.onSend?.(ip, cmd, data, 0, "socket not ready");
      return;
    }
    const message: LanMessage = {
      msg: { cmd, data },
    };
    const buf = Buffer.from(JSON.stringify(message));
    // L5 — the Govee spec is UDP-PMTU-bound. Warn on very large ptReal payloads
    // (many BLE packets in JSON) so the user knows why something gets lost.
    if (buf.length > 1400) {
      this.log.debug(`LAN payload large (${buf.length} bytes) — may be PMTU-fragmented for ${ip}`);
    }
    this.sendSocket.send(buf, 0, buf.length, COMMAND_PORT, ip, err => {
      if (err) {
        this.log.debug(`LAN send error to ${ip}: ${err.message}`);
        this.onSend?.(ip, cmd, data, buf.length, err.message);
      } else {
        this.lastCommandSentMs.set(ip, Date.now());
        this.onSend?.(ip, cmd, data, buf.length);
      }
    });
  }

  /**
   * Send power command
   *
   * @param ip Device IP address
   * @param on Power state
   */
  setPower(ip: string, on: boolean): void {
    this.sendCommand(ip, "turn", { value: on ? 1 : 0 });
  }

  /**
   * Send brightness command
   *
   * @param ip Device IP address
   * @param brightness Brightness 0-100
   */
  setBrightness(ip: string, brightness: number): void {
    this.sendCommand(ip, "brightness", {
      value: clampByte0_100(brightness),
    });
  }

  /**
   * Send color command. Inputs are clamped to 0-255 — out-of-range values
   * from upstream coercion paths (capability-mapper, command-router) would
   * otherwise be sent verbatim and produce undefined-behaviour at the device.
   *
   * @param ip Device IP address
   * @param r Red channel 0-255
   * @param g Green channel 0-255
   * @param b Blue channel 0-255
   */
  setColor(ip: string, r: number, g: number, b: number): void {
    this.sendCommand(ip, "colorwc", {
      color: { r: clampByte(r), g: clampByte(g), b: clampByte(b) },
      colorTemInKelvin: 0,
    });
  }

  /**
   * Send color temperature command. Out-of-band kelvin values are clamped
   * to Govee's published 2000-9000 K range (per-device range may be tighter,
   * those are corrected via {@link applyColorTempQuirk} upstream).
   *
   * @param ip Device IP address
   * @param kelvin Color temperature in Kelvin
   */
  setColorTemperature(ip: string, kelvin: number): void {
    const clamped = Number.isFinite(kelvin) ? Math.max(2000, Math.min(9000, Math.round(kelvin))) : 2000;
    this.sendCommand(ip, "colorwc", {
      color: { r: 0, g: 0, b: 0 },
      colorTemInKelvin: clamped,
    });
  }

  /**
   * Send a scene via ptReal BLE-passthrough.
   * Builds multi-packet BLE data from scenceParam + final scene-code packet.
   *
   * @param ip Device IP address
   * @param sceneCode Scene code from scene library (must be > 0)
   * @param scenceParam Base64-encoded scene parameter data (may be empty for simple presets)
   */
  setScene(ip: string, sceneCode: number, scenceParam: string): void {
    if (sceneCode <= 0) {
      return;
    }
    const packets = buildScenePackets(sceneCode, scenceParam);
    this.sendPtReal(ip, packets);
  }

  /**
   * Send raw ptReal BLE-passthrough packets to a device.
   *
   * @param ip Device IP address
   * @param base64Packets Array of Base64-encoded 20-byte BLE packets
   */
  sendPtReal(ip: string, base64Packets: string[]): void {
    if (!this.sendSocket) {
      this.log.debug(`LAN ptReal dropped (socket not ready): ${ip}`);
      this.onSend?.(ip, "ptReal", { command: base64Packets }, 0, "socket not ready");
      return;
    }
    const message = {
      msg: { cmd: "ptReal", data: { command: base64Packets } },
    };
    const buf = Buffer.from(JSON.stringify(message));
    if (buf.length > 1400) {
      this.log.debug(`ptReal payload large (${buf.length} bytes) — may be PMTU-fragmented for ${ip}`);
    }
    this.sendSocket.send(buf, 0, buf.length, COMMAND_PORT, ip, err => {
      if (err) {
        // warn (was: debug) — a silent UDP-send failure leaves the user
        // wondering why a scene/snapshot "did nothing". One log per failed
        // send is acceptable noise; recurring sends to the same offline IP
        // will repeat, which is the right signal.
        this.log.warn(`LAN ptReal error to ${ip}: ${err.message}`);
        this.onSend?.(ip, "ptReal", { command: base64Packets }, buf.length, err.message);
      } else {
        // Success on debug: confirms the UDP datagram left the socket so
        // a "snapshot/scene did not activate" report can be triaged
        // without enabling silly-level wire logging.
        this.log.debug(`LAN ptReal sent to ${ip}: ${base64Packets.length} packet(s), ${buf.length} bytes`);
        this.lastCommandSentMs.set(ip, Date.now());
        this.onSend?.(ip, "ptReal", { command: base64Packets }, buf.length);
      }
    });
  }

  /**
   * Set gradient toggle via ptReal BLE-passthrough.
   *
   * @param ip Device IP address
   * @param on Gradient on/off
   */
  setGradient(ip: string, on: boolean): void {
    this.sendPtReal(ip, [buildGradientPacket(on)]);
  }

  /**
   * Activate a DIY scene via ptReal BLE-passthrough.
   * Sends A1 multi-packet data (if provided) + activation command.
   *
   * @param ip Device IP address
   * @param scenceParam Base64-encoded DIY parameter data (may be empty to activate last DIY)
   */
  setDiyScene(ip: string, scenceParam: string): void {
    const packets = buildDiyPackets(scenceParam);
    this.sendPtReal(ip, packets);
  }

  /**
   * Set music mode via ptReal BLE-passthrough. Whether RGB is sent is decided
   * by the caller via the mode NAME (Spectrum/Rolling), not the value.
   *
   * @param ip Device IP address
   * @param subMode Music sub-mode value (raw capability value)
   * @param includeRgb Whether this mode carries a custom RGB colour (Spectrum/Rolling)
   * @param r Red channel 0-255 (used when includeRgb)
   * @param g Green channel 0-255
   * @param b Blue channel 0-255
   */
  setMusicMode(ip: string, subMode: number, includeRgb: boolean, r = 0, g = 0, b = 0): void {
    this.sendPtReal(ip, [buildMusicModePacket(subMode, includeRgb, r, g, b)]);
  }

  /**
   * Set segment color via ptReal BLE-passthrough (command 33 05 15 01).
   *
   * @param ip Device IP address
   * @param r Red 0-255
   * @param g Green 0-255
   * @param b Blue 0-255
   * @param segments Array of 0-based segment indices
   */
  setSegmentColor(ip: string, r: number, g: number, b: number, segments: number[]): void {
    this.sendPtReal(ip, [buildSegmentColorPacket(r, g, b, segments)]);
  }

  /**
   * Set segment brightness via ptReal BLE-passthrough (command 33 05 15 02).
   *
   * @param ip Device IP address
   * @param brightness Brightness 0-100
   * @param segments Array of 0-based segment indices
   */
  setSegmentBrightness(ip: string, brightness: number, segments: number[]): void {
    this.sendPtReal(ip, [buildSegmentBrightnessPacket(brightness, segments)]);
  }

  /**
   * Flash a single segment bright white and dim all other segments, in ONE
   * atomic ptReal transmission. All three required BLE packets are bundled
   * into a single UDP datagram so the device cannot drop intermediate steps.
   *
   * The "dim everything else" packet targets the full bitmask width (56
   * segments — the Govee protocol's upper bound: 7 bytes × 8 bits). This
   * covers under-report cases where the Cloud says "15 segments" but the
   * strip physically has more. Without this the unreported segments keep
   * shining at whatever brightness they had before the wizard started.
   *
   * Packet order:
   *   0. `colorwc` — force static-color mode (segment_color_setting packets
   *      are ignored while the device is in Scene/Gradient/Music mode)
   *   1. All segments except idx (up to idx 55) → brightness 0
   *   2. Target segment → color 0xFFFFFF (full white)
   *   3. Target segment → brightness 100 (make it bright)
   *
   * @param ip Device IP address
   * @param idx Target segment index (0-based) to flash white
   */
  flashSingleSegment(ip: string, idx: number): void {
    if (idx < 0 || idx >= SEGMENT_COUNT_MAX) {
      return;
    }
    const MAX_SEGMENTS = SEGMENT_COUNT_MAX;
    const others = Array.from({ length: MAX_SEGMENTS }, (_, i) => i).filter(i => i !== idx);
    // Step 0: force color mode. Without this, the strip stays in whatever
    // mode it was (Scene/Gradient/Music) and silently ignores the three
    // ptReal packets below. The colorwc command resets to a known static
    // state that accepts segment-level overrides.
    this.setColor(ip, 0xff, 0xff, 0xff);
    // Small delay so the firmware can apply the mode switch before the
    // next UDP burst — Govee's observed minimum is ~50 ms. The handle is
    // tracked so stop() can cancel it explicitly: ioBroker timers eventually
    // fire into a torn-down adapter otherwise (the wrapper-tracked handles
    // get cleared in onUnload, but only via this Set).
    const delayMs = FORCE_COLOR_MODE_SETTLE_MS;
    const handle = this.timers.setTimeout(() => {
      if (handle !== undefined) {
        this.pendingFlashTimers.delete(handle);
      }
      if (this.stopped || !this.sendSocket) {
        return;
      }
      this.sendPtReal(ip, [
        buildSegmentBrightnessPacket(0, others),
        buildSegmentColorPacket(0xff, 0xff, 0xff, [idx]),
        buildSegmentBrightnessPacket(100, [idx]),
      ]);
    }, delayMs);
    if (handle !== undefined) {
      this.pendingFlashTimers.add(handle);
    }
  }

  /**
   * Restore a segment strip to a uniform color + brightness in one atomic
   * ptReal transmission. Used at wizard end/abort to put the strip back to
   * the captured baseline.
   *
   * @param ip Device IP address
   * @param total Total number of segments
   * @param r Red 0-255
   * @param g Green 0-255
   * @param b Blue 0-255
   * @param brightness Brightness 0-100
   */
  restoreAllSegments(ip: string, total: number, r: number, g: number, b: number, brightness: number): void {
    if (total <= 0) {
      return;
    }
    const all = Array.from({ length: total }, (_, i) => i);
    this.sendPtReal(ip, [buildSegmentColorPacket(r, g, b, all), buildSegmentBrightnessPacket(brightness, all)]);
  }

  /**
   * Request device status
   *
   * @param ip Device IP address
   */
  requestStatus(ip: string): void {
    this.sendCommand(ip, "devStatus", {});
  }

  /** Send multicast scan */
  private sendScan(): void {
    const scanMsg: LanMessage = {
      msg: { cmd: "scan", data: { account_topic: "reserve" } },
    };
    const buf = Buffer.from(JSON.stringify(scanMsg));
    this.scanSocket?.send(buf, 0, buf.length, SCAN_PORT, MULTICAST_ADDR, err => {
      if (err) {
        this.log.debug(`LAN scan send error: ${err.message}`);
      }
    });
  }

  /**
   * Parse incoming UDP message
   *
   * @param msg Raw UDP message buffer
   * @param sourceIp Source IP address from UDP rinfo
   */
  private handleMessage(msg: Buffer, sourceIp: string): void {
    // L9 — size bound. Pathological packets (several reassembled UDP frames)
    // could be 64KB+. JSON.parse of 64KB+ blocks the event loop for ms spans —
    // noticeable with many devices at once.
    if (msg.length > 8192) {
      this.log.debug(`LAN message dropped from ${sourceIp}: oversize ${msg.length} bytes`);
      return;
    }
    try {
      const data = JSON.parse(msg.toString()) as {
        msg?: { cmd?: string; data?: Record<string, unknown> };
      };
      if (!data.msg?.cmd || typeof data.msg.cmd !== "string") {
        return;
      }

      const cmd: string = data.msg.cmd;
      const rawPayload = data.msg.data;
      const payload: Record<string, unknown> =
        rawPayload && typeof rawPayload === "object" && !Array.isArray(rawPayload) ? rawPayload : {};

      if (cmd === "scan") {
        this.handleScanResponse(payload, sourceIp);
      } else if (cmd === "devStatus") {
        this.handleStatusResponse(payload, sourceIp);
      }
    } catch {
      this.log.debug(`LAN: Failed to parse message: ${msg.toString().slice(0, 200)}`);
    }
  }

  /**
   * Handle scan response — new device found. The device's IP is taken from the
   * UDP source address, NOT the attacker-controllable `data.ip` payload field
   * (SEC-M1) — otherwise a spoofed scan reply could redirect a device's
   * outbound commands to an attacker-chosen IP.
   *
   * @param data Parsed scan response payload
   * @param sourceIp Source IP from the UDP rinfo — the authentic device address
   */
  private handleScanResponse(data: Record<string, unknown>, sourceIp: string): void {
    // Defensive type checks — LAN payload comes over the wire, treat as untrusted
    if (
      typeof data.ip !== "string" ||
      typeof data.device !== "string" ||
      typeof data.sku !== "string" ||
      !data.ip ||
      !data.device ||
      !data.sku
    ) {
      return;
    }

    // Length bounds — a spoofed-discovery flood could carry absurdly padded
    // device/sku strings; a real Govee deviceId is <=47 chars, a SKU <=~8 (SEC-H2).
    if (data.device.length > 64 || data.sku.length > 24) {
      return;
    }

    const lanDevice: LanDevice = {
      // data.ip is validated above as part of a well-formed reply but is NOT
      // trusted for the binding — the authentic address is the UDP source.
      ip: sourceIp,
      device: data.device,
      sku: data.sku,
    };

    const key = `${lanDevice.device}:${lanDevice.ip}`;
    if (!this.seenDeviceIps.has(key)) {
      // Cap distinct LAN identities so a spoofed-discovery flood can't grow the
      // set (and the downstream device map) without bound (SEC-H2). Known
      // devices always pass; only brand-new identities beyond the cap drop.
      if (this.seenDeviceIps.size >= MAX_DISTINCT_LAN_DEVICES) {
        if (!this.lanFloodWarned) {
          this.lanFloodWarned = true;
          this.log.warn(
            `LAN discovery cap reached (${MAX_DISTINCT_LAN_DEVICES}) — ignoring further new devices; check for a misbehaving or hostile device on your network`,
          );
        }
        return;
      }
      // Evict any stale entries for the same device at different IPs so the
      // set stays bounded by the actual number of devices, not the full
      // history of IPs they ever had.
      const staleSuffix = `${lanDevice.device}:`;
      for (const existing of this.seenDeviceIps) {
        if (existing.startsWith(staleSuffix) && existing !== key) {
          this.seenDeviceIps.delete(existing);
          // The command-timestamp map is keyed by IP — drop the entry for the
          // device's abandoned IP so the map stays bounded by live addresses.
          this.lastCommandSentMs.delete(existing.slice(staleSuffix.length));
        }
      }
      this.seenDeviceIps.add(key);
      this.log.debug(`LAN: Found ${lanDevice.sku} (${lanDevice.device}) at ${lanDevice.ip}`);
    }
    this.onScanRecord?.(lanDevice);
    this.onDiscovery?.(lanDevice);
  }

  /**
   * Handle status response — matched to device by source IP.
   * Defensive against malformed/partial payloads — all fields coerced to safe defaults.
   *
   * @param data Parsed status response payload
   * @param sourceIp Source IP address from UDP message
   */
  private handleStatusResponse(data: Record<string, unknown>, sourceIp: string): void {
    const toNum = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
    const colorRaw = data.color;
    const color =
      colorRaw && typeof colorRaw === "object"
        ? {
            r: toNum((colorRaw as Record<string, unknown>).r),
            g: toNum((colorRaw as Record<string, unknown>).g),
            b: toNum((colorRaw as Record<string, unknown>).b),
          }
        : { r: 0, g: 0, b: 0 };

    const status: LanStatus = {
      onOff: toNum(data.onOff),
      brightness: toNum(data.brightness),
      color,
      colorTemInKelvin: toNum(data.colorTemInKelvin),
    };

    // Approximate "did this follow a command of ours?" signal — small Δ is
    // *probably* a response, but UDP carries no command ID so it can't be
    // proven. Honest framing: proximity, not proof.
    const lastSend = this.lastCommandSentMs.get(sourceIp);
    if (lastSend !== undefined) {
      const dt = Date.now() - lastSend;
      this.log.debug(`LAN status from ${sourceIp}: Δ ${dt}ms since last command (proximity, not ack)`);
    }

    this.onStatusRecord?.(sourceIp, status);
    this.onStatus?.(sourceIp, status);
  }
}

// --- BLE Packet Builder for ptReal ---

/**
 * Clamp a value to 0-100. NaN / non-numeric → 0.
 *
 * @param v Input value
 */
function clampByte0_100(v: number): number {
  if (typeof v !== "number" || !Number.isFinite(v)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(v)));
}

/**
 * XOR checksum over all bytes
 *
 * @param data Array of byte values
 */
function xorChecksum(data: number[]): number {
  let checksum = 0;
  for (const b of data) {
    checksum ^= b;
  }
  return checksum;
}

/**
 * Pad data to 19 bytes + append XOR checksum = 20-byte BLE packet
 *
 * @param data Array of byte values to pad and checksum
 */
function finishPacket(data: number[]): number[] {
  while (data.length < 19) {
    data.push(0);
  }
  data.push(xorChecksum(data));
  return data;
}

/**
 * Frame arbitrary payload bytes into 19-byte BLE packets using Govee's
 * line-continuation protocol, then base64 each. Scenes (A3) and DIY (A1) differ
 * only in the header bytes, the per-line continuation prefix, and which header
 * index the 0xff line-marker defaults to when the payload fits a single line —
 * the `% 19` chunking, the `rawData[3] = numLines + 1` count, and the chunk →
 * checksum → base64 tail are identical. Byte-locked by the
 * "A-frame packet framing (byte-golden)" test.
 *
 * @param paramBytes Decoded payload bytes
 * @param header Leading frame bytes (the line count is written at index 3)
 * @param contPrefix Bytes that open each continuation line (before its number)
 * @param initMarker Header index the 0xff marker defaults to for a single line
 */
function buildAFramedPackets(
  paramBytes: number[],
  header: number[],
  contPrefix: number[],
  initMarker: number,
): string[] {
  const rawData: number[] = [...header];
  let numLines = 0;
  let lastLineMarker = initMarker;

  for (const b of paramBytes) {
    if (rawData.length % 19 === 0) {
      numLines++;
      rawData.push(...contPrefix);
      lastLineMarker = rawData.length;
      rawData.push(numLines);
    }
    rawData.push(b);
  }
  rawData[lastLineMarker] = 0xff;
  rawData[3] = numLines + 1;

  // Split into 19-byte chunks, pad + checksum each
  const packets: string[] = [];
  for (let i = 0; i < rawData.length; i += 19) {
    packets.push(Buffer.from(finishPacket(rawData.slice(i, i + 19))).toString("base64"));
  }
  return packets;
}

/**
 * Build Base64-encoded BLE packets for scene activation via ptReal.
 *
 * @param sceneCode Scene code from library (> 0)
 * @param scenceParam Base64-encoded scene parameter data (may be empty)
 */
export function buildScenePackets(sceneCode: number, scenceParam: string): string[] {
  const packets: string[] = [];

  // Multi-packet scene data (A3 framing: header A3 00 01 00 02, continuation A3)
  if (scenceParam) {
    const paramBytes = Array.from(Buffer.from(scenceParam, "base64"));
    packets.push(...buildAFramedPackets(paramBytes, [0xa3, 0x00, 0x01, 0x00, 0x02], [0xa3], 1));
  }

  // Final scene-code activation packet: 33 05 04 lo hi
  const lo = sceneCode & 0xff;
  const hi = (sceneCode >> 8) & 0xff;
  const activatePacket = finishPacket([0x33, 0x05, 0x04, lo, hi]);
  packets.push(Buffer.from(activatePacket).toString("base64"));

  return packets;
}

/**
 * Build Base64-encoded BLE packets for DIY scene activation via ptReal.
 * Uses A1 framing for multi-packet data, then sends activation command.
 *
 * @param scenceParam Base64-encoded DIY parameter data (may be empty)
 */
export function buildDiyPackets(scenceParam: string): string[] {
  const packets: string[] = [];

  // Multi-packet DIY data (A1 framing: header A1 02 00 00, continuation A1 02)
  if (scenceParam) {
    const paramBytes = Array.from(Buffer.from(scenceParam, "base64"));
    packets.push(...buildAFramedPackets(paramBytes, [0xa1, 0x02, 0x00, 0x00], [0xa1, 0x02], 2));
  }

  // Activation: 33 05 0A
  packets.push(Buffer.from(finishPacket([0x33, 0x05, 0x0a])).toString("base64"));
  return packets;
}

/**
 * Build a Base64-encoded BLE packet for gradient toggle via ptReal.
 *
 * @param on Gradient on/off
 */
export function buildGradientPacket(on: boolean): string {
  return Buffer.from(finishPacket([0x33, 0x14, on ? 0x01 : 0x00])).toString("base64");
}

/**
 * Build a Base64-encoded BLE packet for music mode via ptReal
 * (`33 05 01 <mode> [R G B]`).
 *
 * Whether RGB is appended is decided by the CALLER (via {@link
 * musicModeNameUsesRgb} on the mode name), not by the sub-mode value here:
 * Govee's music-mode values are SKU-specific, so a value-based gate would
 * append RGB on the wrong mode for a SKU whose Spectrum/Rolling isn't at the
 * usual value 1/2 (A1 proved mode values vary across the fleet).
 *
 * @param subMode Music sub-mode value sent to the device (raw capability value)
 * @param includeRgb Whether this mode carries a custom RGB colour (Spectrum/Rolling)
 * @param r Red channel 0-255
 * @param g Green channel 0-255
 * @param b Blue channel 0-255
 */
export function buildMusicModePacket(subMode: number, includeRgb: boolean, r = 0, g = 0, b = 0): string {
  const data = [0x33, 0x05, 0x01, subMode & 0xff];
  if (includeRgb) {
    data.push(r & 0xff, g & 0xff, b & 0xff);
  }
  return Buffer.from(finishPacket(data)).toString("base64");
}

/**
 * Build a little-endian segment bitmask.
 * Segment 0 = byte[0] bit 0, Segment 8 = byte[1] bit 0, etc.
 *
 * @param segments Array of 0-based segment indices
 * @param byteCount Number of bitmask bytes (7 for color, 14 for brightness)
 */
export function buildSegmentBitmask(segments: number[], byteCount: number): number[] {
  const mask = new Array<number>(byteCount).fill(0);
  for (const seg of segments) {
    const byteIdx = Math.floor(seg / 8);
    const bitIdx = seg % 8;
    if (byteIdx < byteCount) {
      mask[byteIdx] |= 1 << bitIdx;
    }
  }
  return mask;
}

/**
 * Build a Base64-encoded BLE packet for segment color via ptReal.
 * Command: 33 05 15 01 RR GG BB 00×5 bitmask×7
 *
 * @param r Red 0-255
 * @param g Green 0-255
 * @param b Blue 0-255
 * @param segments Array of 0-based segment indices
 */
export function buildSegmentColorPacket(r: number, g: number, b: number, segments: number[]): string {
  const data = [
    0x33,
    0x05,
    0x15,
    0x01,
    r & 0xff,
    g & 0xff,
    b & 0xff,
    0x00,
    0x00,
    0x00,
    0x00,
    0x00,
    ...buildSegmentBitmask(segments, SEGMENT_COLOR_BITMASK_BYTES),
  ];
  return Buffer.from(finishPacket(data)).toString("base64");
}

/**
 * Build a Base64-encoded BLE packet for segment brightness via ptReal.
 * Command: 33 05 15 02 BB bitmask×14
 *
 * @param brightness Brightness 0-100
 * @param segments Array of 0-based segment indices
 */
export function buildSegmentBrightnessPacket(brightness: number, segments: number[]): string {
  const data = [
    0x33,
    0x05,
    0x15,
    0x02,
    Math.max(0, Math.min(100, brightness)),
    ...buildSegmentBitmask(segments, SEGMENT_BRIGHTNESS_BITMASK_BYTES),
  ];
  return Buffer.from(finishPacket(data)).toString("base64");
}

/**
 * Apply speed level to a scene's scenceParam by replacing speed bytes in each page.
 * scenceParam structure: byte[0] = page count, then per page: 1 byte length + N bytes data.
 * Speed byte position within each page: pageLength - 5.
 *
 * @param scenceParam Base64-encoded scene parameter data
 * @param speedLevel Speed level index (0-based)
 * @param speedConfig JSON config string from speedInfo.config
 * @returns Modified Base64-encoded scenceParam with speed bytes replaced
 */
export function applySceneSpeed(scenceParam: string, speedLevel: number, speedConfig: string): string {
  if (!scenceParam || !speedConfig) {
    return scenceParam;
  }

  let configEntries: Array<{
    page: number;
    moveIn?: number[];
  }>;
  try {
    configEntries = JSON.parse(speedConfig);
  } catch {
    // Govee's speedInfo.config schema can drift — this is a pure helper
    // without a logger, so a malformed config falls back silently: the
    // un-modified scenceParam keeps the activation working at default
    // speed instead of failing the whole scene command.
    return scenceParam;
  }

  if (!Array.isArray(configEntries) || configEntries.length === 0) {
    return scenceParam;
  }

  const bytes = Array.from(Buffer.from(scenceParam, "base64"));
  if (bytes.length === 0) {
    return scenceParam;
  }

  const pageCount = bytes[0];
  let offset = 1;

  for (let pageIdx = 0; pageIdx < pageCount && offset < bytes.length; pageIdx++) {
    const pageLen = bytes[offset];
    if (offset + 1 + pageLen > bytes.length) {
      break;
    }

    const cfg = configEntries.find(c => c.page === pageIdx);
    if (cfg?.moveIn && speedLevel >= 0 && speedLevel < cfg.moveIn.length) {
      const speedBytePos = offset + 1 + (pageLen - 5);
      if (speedBytePos > offset && speedBytePos < offset + 1 + pageLen) {
        bytes[speedBytePos] = cfg.moveIn[speedLevel];
      }
    }

    offset += 1 + pageLen;
  }

  return Buffer.from(bytes).toString("base64");
}
